<template>
  <div class="chips-container">
    <!-- 页面头部 -->
    <div class="page-header">
      <div class="header-content">
        <h1 class="page-title">
          <i class="el-icon-cpu"></i>
          芯片管理
        </h1>
        <p class="page-description">管理系统芯片，包括芯片信息查看、编辑、删除等操作</p>
      </div>
    </div>

    <!-- 搜索和操作区域 -->
    <div class="search-section">
      <el-card class="search-card" shadow="never">
        <div class="search-form">
          <div class="search-row">
            <div class="search-item">
              <el-input
                v-model="searchForm.keyword"
                placeholder="搜索MAC地址或芯片类型"
                prefix-icon="el-icon-search"
                clearable
                @keyup.enter.native="handleSearch"
                class="search-input"
              />
            </div>
            <div class="search-item">
              <el-input
                v-model="searchForm.chip_id"
                placeholder="芯片ID"
                clearable
                class="search-input"
              />
            </div>
            <div class="search-actions">
              <el-button type="primary" icon="el-icon-search" @click="handleSearch">
                搜索
              </el-button>
              <el-button icon="el-icon-refresh" @click="handleReset">
                重置
              </el-button>
            </div>
          </div>
        </div>
      </el-card>
    </div>

    <!-- 操作按钮区域 -->
    <div class="action-section">
      <el-card class="action-card" shadow="never">
        <div class="action-buttons">
          <el-button
            type="primary"
            icon="el-icon-plus"
            @click="handleCreate"
            class="create-btn"
          >
            新增芯片
          </el-button>
          <el-button
            type="danger"
            icon="el-icon-delete"
            :disabled="selectedChips.length === 0"
            @click="handleBatchDelete"
            class="batch-delete-btn"
          >
            批量删除 ({{ selectedChips.length }})
          </el-button>
        </div>
      </el-card>
    </div>

    <!-- 数据表格 -->
    <div class="table-section">
      <el-card class="table-card" shadow="never">
        <el-table
          :data="chipList"
          v-loading="loading"
          @selection-change="handleSelectionChange"
          stripe
          class="chip-table"
          :header-cell-style="{ background: '#f5f7fa', color: '#606266', textAlign: 'center' }"
        >
          <el-table-column type="selection" width="55" align="center" />
          <el-table-column prop="id" label="芯片ID" width="80" align="center" />
          <el-table-column prop="mac_address" label="MAC地址" min-width="180" align="center">
            <template slot-scope="scope">
              <div class="mac-cell">
                <i class="el-icon-postcard"></i>
                <span>{{ scope.row.mac_address }}</span>
              </div>
            </template>
          </el-table-column>
          <el-table-column prop="chip_type" label="芯片类型" min-width="150" align="center">
            <template slot-scope="scope">
              <el-tag type="info" size="medium">{{ scope.row.chip_type }}</el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="remark" label="备注" min-width="200" align="center">
            <template slot-scope="scope">
              <span class="remark-text">{{ scope.row.remark || '-' }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="created_at" label="创建时间" width="180" align="center">
            <template slot-scope="scope">
              <div class="time-cell">
                <i class="el-icon-time"></i>
                <span>{{ formatDate(scope.row.created_at) }}</span>
              </div>
            </template>
          </el-table-column>
          <el-table-column label="操作" width="300" fixed="right" align="center">
            <template slot-scope="scope">
              <div class="action-buttons">
                <el-button
                  type="text"
                  icon="el-icon-view"
                  @click="handleViewRobots(scope.row)"
                  class="action-btn view-btn"
                >
                  查看详情
                </el-button>
                <el-button
                  type="text"
                  icon="el-icon-edit"
                  @click="handleEdit(scope.row)"
                  class="action-btn edit-btn"
                >
                  编辑
                </el-button>
                <el-button
                  type="text"
                  icon="el-icon-delete"
                  @click="handleDelete(scope.row)"
                  class="action-btn delete-btn"
                >
                  删除
                </el-button>
              </div>
            </template>
          </el-table-column>
        </el-table>

        <!-- 分页 -->
        <div class="pagination-wrapper">
          <el-pagination
            @size-change="handleSizeChange"
            @current-change="handleCurrentChange"
            :current-page="pagination.page"
            :page-sizes="[10, 20, 50, 100]"
            :page-size="pagination.pageSize"
            layout="total, sizes, prev, pager, next, jumper"
            :total="pagination.total"
            class="pagination"
          />
        </div>
      </el-card>
    </div>

    <!-- 芯片关联机器人对话框 -->
    <el-dialog
      title="关联机器人"
      :visible.sync="robotDialogVisible"
      width="700px"
      class="robot-dialog"
    >
      <div v-if="currentChip" class="robot-detail">
        <div class="chip-info">
          <h3>芯片信息</h3>
          <div class="info-grid">
            <div class="info-item">
              <label>MAC地址：</label>
              <span>{{ currentChip.mac_address }}</span>
            </div>
            <div class="info-item">
              <label>芯片类型：</label>
              <span>{{ currentChip.chip_type }}</span>
            </div>
          </div>
        </div>
        
        <div class="robot-list-section">
          <h3>关联的机器人列表</h3>
          <el-table
            :data="relatedRobots"
            v-loading="robotLoading"
            stripe
            class="robot-table"
            :header-cell-style="{ background: '#f5f7fa', color: '#606266' }"
          >
            <el-table-column prop="robot_id" label="机器人ID" width="100" />
            <el-table-column prop="robot_name" label="机器人名称" min-width="150" />
            <el-table-column prop="user_id" label="用户ID" width="100" />
            <el-table-column prop="username" label="用户名称" min-width="150" />
          </el-table>
          <el-empty v-if="relatedRobots.length === 0 && !robotLoading" description="暂无关联的机器人"></el-empty>
        </div>
      </div>
    </el-dialog>

    <!-- 新增/编辑芯片对话框 -->
    <el-dialog
      :title="dialogTitle"
      :visible.sync="formDialogVisible"
      width="500px"
      class="form-dialog"
    >
      <el-form
        :model="chipForm"
        :rules="formRules"
        ref="chipForm"
        label-width="100px"
        class="chip-form"
      >
        <el-form-item label="MAC地址" prop="mac_address" v-if="!isEdit">
          <el-input v-model="chipForm.mac_address" placeholder="请输入MAC地址" />
        </el-form-item>
        <el-form-item label="芯片类型" prop="chip_type">
          <el-input v-model="chipForm.chip_type" placeholder="请输入芯片类型" />
        </el-form-item>
        <el-form-item label="备注" prop="remark">
          <el-input
            v-model="chipForm.remark"
            type="textarea"
            :rows="4"
            placeholder="请输入备注信息"
          />
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="formDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleSubmit" :loading="submitLoading">
          确定
        </el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getChipList, getChipRobots, createChip, updateChip, deleteChip, batchDeleteChips } from '@/api/chip'

export default {
  name: 'AdminChips',
  data() {
    return {
      loading: false,
      chipList: [],
      selectedChips: [],
      searchForm: {
        keyword: '',
        chip_id: ''
      },
      pagination: {
        page: 1,
        pageSize: 10,
        total: 0
      },
      robotDialogVisible: false,
      formDialogVisible: false,
      isEdit: false,
      currentChip: null,
      relatedRobots: [],
      robotLoading: false,
      chipForm: {
        mac_address: '',
        chip_type: '',
        remark: ''
      },
      formRules: {
        mac_address: [
          { required: true, message: '请输入MAC地址', trigger: 'blur' },
          { min: 1, max: 50, message: 'MAC地址长度不能超过50个字符', trigger: 'blur' }
        ],
        chip_type: [
          { required: true, message: '请输入芯片类型', trigger: 'blur' },
          { min: 1, max: 50, message: '芯片类型长度不能超过50个字符', trigger: 'blur' }
        ],
        remark: [
          { max: 255, message: '备注长度不能超过255个字符', trigger: 'blur' }
        ]
      },
      submitLoading: false
    }
  },
  computed: {
    dialogTitle() {
      return this.isEdit ? '编辑芯片' : '新增芯片'
    }
  },
  created() {
    this.getChipList()
  },
  methods: {
    // 获取芯片列表
    async getChipList() {
      this.loading = true
      try {
        const params = {
          page: this.pagination.page,
          pageSize: this.pagination.pageSize,
          keyword: this.searchForm.keyword,
          chip_id: this.searchForm.chip_id
        }
        const response = await getChipList(params)
        this.chipList = response.data.list || []
        this.pagination.total = response.data.total
      } finally {
        this.loading = false
      }
    },

    // 搜索
    handleSearch() {
      this.pagination.page = 1
      this.getChipList()
    },

    // 重置搜索
    handleReset() {
      this.searchForm = {
        keyword: '',
        chip_id: ''
      }
      this.pagination.page = 1
      this.getChipList()
    },

    // 分页大小改变
    handleSizeChange(size) {
      this.pagination.pageSize = size
      this.pagination.page = 1
      this.getChipList()
    },

    // 当前页改变
    handleCurrentChange(page) {
      this.pagination.page = page
      this.getChipList()
    },

    // 选择改变
    handleSelectionChange(selection) {
      this.selectedChips = selection
    },

    // 查看关联的机器人
    async handleViewRobots(chip) {
      this.currentChip = chip
      this.robotDialogVisible = true
      this.robotLoading = true
      try {
        const response = await getChipRobots(chip.id)
        this.relatedRobots = response.data || []
      } finally {
        this.robotLoading = false
      }
    },

    // 新增芯片
    handleCreate() {
      this.isEdit = false
      this.chipForm = {
        mac_address: '',
        chip_type: '',
        remark: ''
      }
      this.formDialogVisible = true
      this.$nextTick(() => {
        this.$refs.chipForm.clearValidate()
      })
    },

    // 编辑芯片
    handleEdit(chip) {
      this.isEdit = true
      this.currentChip = chip
      this.chipForm = {
        mac_address: chip.mac_address,
        chip_type: chip.chip_type,
        remark: chip.remark || ''
      }
      this.formDialogVisible = true
      this.$nextTick(() => {
        this.$refs.chipForm.clearValidate()
      })
    },

    // 提交表单
    async handleSubmit() {
      try {
        await this.$refs.chipForm.validate()
        this.submitLoading = true

        if (this.isEdit) {
          // 编辑时只提交 chip_type 和 remark
          await updateChip(this.currentChip.id, {
            chip_type: this.chipForm.chip_type,
            remark: this.chipForm.remark
          })
          this.$message.success('芯片更新成功')
        } else {
          await createChip(this.chipForm)
          this.$message.success('芯片创建成功')
        }

        this.formDialogVisible = false
        this.getChipList()
      } finally {
        this.submitLoading = false
      }
    },

    // 删除芯片
    handleDelete(chip) {
      this.$confirm(`确定要删除MAC地址为 "${chip.mac_address}" 的芯片吗？`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        await deleteChip(chip.id)
        this.$message.success('删除成功')
        this.getChipList()
      })
    },

    // 批量删除
    handleBatchDelete() {
      if (this.selectedChips.length === 0) {
        this.$message.warning('请选择要删除的芯片')
        return
      }

      this.$confirm(`确定要删除选中的 ${this.selectedChips.length} 个芯片吗？`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        const ids = this.selectedChips.map(chip => chip.id)
        await batchDeleteChips(ids)
        this.$message.success('批量删除成功')
        this.getChipList()
      })
    },

    // 格式化日期
    formatDate(dateString) {
      if (!dateString) return '-'
      const date = new Date(dateString)
      return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    }
  }
}
</script>

<style scoped>
.chips-container {
  padding: 20px;
  background-color: #f5f7fa;
  min-height: 100vh;
}

/* 页面头部 */
.page-header {
  margin-bottom: 20px;
}

.header-content {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 30px;
  border-radius: 12px;
  color: white;
  box-shadow: 0 4px 20px rgba(102, 126, 234, 0.3);
}

.page-title {
  margin: 0 0 10px 0;
  font-size: 28px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 12px;
}

.page-description {
  margin: 0;
  opacity: 0.9;
  font-size: 16px;
}

/* 搜索区域 */
.search-section {
  margin-bottom: 20px;
}

.search-card {
  border-radius: 12px;
  border: none;
}

.search-form {
  padding: 10px 0;
}

.search-row {
  display: flex;
  gap: 16px;
  align-items: center;
  flex-wrap: wrap;
}

.search-item {
  flex: 1;
  min-width: 200px;
}

.search-input {
  width: 100%;
}

.search-actions {
  display: flex;
  gap: 12px;
}

/* 操作区域 */
.action-section {
  margin-bottom: 20px;
}

.action-card {
  border-radius: 12px;
  border: none;
}

.action-section .action-buttons {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  justify-content: flex-start;
}

.create-btn {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  border-radius: 8px;
  padding: 12px 24px;
  font-weight: 500;
}

.batch-delete-btn {
  border-radius: 8px;
  padding: 12px 24px;
  font-weight: 500;
}

/* 表格区域 */
.table-section {
  margin-bottom: 20px;
}

.table-card {
  border-radius: 12px;
  border: none;
  overflow: hidden;
}

.chip-table {
  width: 100%;
}

.mac-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: #606266;
  font-family: 'Courier New', monospace;
}

.remark-text {
  color: #909399;
  font-size: 14px;
}

.time-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: #909399;
  font-size: 14px;
}

.table-section .action-buttons {
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
  justify-content: center;
}

.action-btn {
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 13px;
  transition: all 0.3s;
}

.view-btn {
  color: #409eff;
}

.view-btn:hover {
  background-color: #ecf5ff;
  color: #337ecc;
}

.edit-btn {
  color: #67c23a;
}

.edit-btn:hover {
  background-color: #f0f9ff;
  color: #529b2e;
}

.delete-btn {
  color: #f56c6c;
}

.delete-btn:hover {
  background-color: #fef0f0;
  color: #c45656;
}

/* 分页 */
.pagination-wrapper {
  display: flex;
  justify-content: center;
  margin-top: 20px;
  padding: 20px 0;
}

.pagination {
  background: white;
  padding: 16px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

/* 对话框 */
.robot-dialog .el-dialog__body {
  padding: 20px;
}

.robot-detail {
  padding: 10px 0;
}

.chip-info {
  margin-bottom: 30px;
}

.chip-info h3 {
  margin: 0 0 15px 0;
  color: #303133;
  font-size: 16px;
  font-weight: 600;
  border-bottom: 2px solid #e4e7ed;
  padding-bottom: 8px;
}

.info-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.info-item {
  display: flex;
  align-items: center;
  padding: 10px;
  background: #f8f9fa;
  border-radius: 6px;
  border-left: 3px solid #667eea;
}

.info-item label {
  font-weight: 600;
  color: #606266;
  margin-right: 8px;
  min-width: 80px;
}

.info-item span {
  color: #303133;
}

.robot-list-section h3 {
  margin: 0 0 15px 0;
  color: #303133;
  font-size: 16px;
  font-weight: 600;
  border-bottom: 2px solid #e4e7ed;
  padding-bottom: 8px;
}

.robot-table {
  width: 100%;
  margin-bottom: 10px;
}

.form-dialog .el-dialog__body {
  padding: 20px;
}

.chip-form {
  padding: 20px 0;
}

.dialog-footer {
  text-align: right;
  padding-top: 20px;
  border-top: 1px solid #e4e7ed;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .chips-container {
    padding: 10px;
  }

  .page-title {
    font-size: 24px;
  }

  .search-row {
    flex-direction: column;
    align-items: stretch;
  }

  .search-item {
    min-width: auto;
  }

  .search-actions {
    justify-content: center;
  }

  .action-section .action-buttons {
    justify-content: flex-start;
  }

  .info-grid {
    grid-template-columns: 1fr;
  }

  .table-section .action-buttons {
    flex-direction: column;
    gap: 4px;
  }

  .action-btn {
    width: 100%;
    text-align: center;
  }
}

@media (max-width: 480px) {
  .header-content {
    padding: 20px;
  }

  .page-title {
    font-size: 20px;
  }

  .page-description {
    font-size: 14px;
  }

  .el-table {
    font-size: 14px;
  }

  .el-dialog {
    width: 95% !important;
    margin: 0 auto;
  }
}
</style>

